import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';

/**
 * Data-fetching and mutation logic for the Users admin page.
 *
 * Owns the users list, unpaid-periods list, onboarding applications list,
 * loading/error state, and the mutation handlers (create user, update
 * renewal date, update member-since date). `filterType` is passed in from
 * the page so the hook can mirror the original page's effect that loads
 * unpaid-periods/onboarding data only when that filter is active.
 */
export function useUsers(filterType) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unpaidUsers, setUnpaidUsers] = useState([]);
  const [onboardingUsers, setOnboardingUsers] = useState([]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsers();
      setUsers(response.data);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUnpaidPeriods = useCallback(async () => {
    try {
      const response = await apiService.getUnpaidPeriods();
      setUnpaidUsers(response.data);
    } catch (err) {
      console.error('Failed to load unpaid periods:', err);
    }
  }, []);

  const loadOnboardingUsers = useCallback(async () => {
    try {
      const response = await apiService.get('/onboarding/applications/active');
      setOnboardingUsers(response.data);
    } catch (err) {
      console.error('Failed to load onboarding users:', err);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadOnboardingUsers(); // Load for stats count
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filterType === 'unpaid-periods') {
      loadUnpaidPeriods();
    } else if (filterType === 'onboarding') {
      loadOnboardingUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType]);

  const createUser = useCallback(async (newUser) => {
    await apiService.createUser(newUser);
    await loadUsers();
  }, [loadUsers]);

  const saveRenewalDate = useCallback(async (userId, dayOfMonth) => {
    await apiService.updateMembershipSettings(userId, {
      renewalDayOfMonth: dayOfMonth
    });
    setUsers(prev => prev.map(u =>
      u.id === userId ? { ...u, renewal_day_of_month: dayOfMonth } : u
    ));
  }, []);

  const saveMemberSince = useCallback(async (userId, value) => {
    await apiService.updateMembershipSettings(userId, {
      startDate: value
    });
    setUsers(prev => prev.map(u =>
      u.id === userId ? { ...u, start_date: value } : u
    ));
    if (filterType === 'unpaid-periods') {
      loadUnpaidPeriods();
    }
  }, [filterType, loadUnpaidPeriods]);

  const getUnpaidMonthsForUser = useCallback((userId) => {
    const unpaidUser = unpaidUsers.find(u => u.id === userId);
    return unpaidUser ? unpaidUser.unpaid_months : [];
  }, [unpaidUsers]);

  return {
    users,
    setUsers,
    loading,
    error,
    unpaidUsers,
    onboardingUsers,
    loadUsers,
    loadUnpaidPeriods,
    loadOnboardingUsers,
    createUser,
    saveRenewalDate,
    saveMemberSince,
    getUnpaidMonthsForUser
  };
}

export default useUsers;
